import { memo, useEffect, useMemo, useRef } from 'react'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { sendEvent, makeEventTrack } from 'src/utils/restructure/analytics'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import Icon from 'src/components/ui/Icon'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { SsrOnlyContent } from 'src/components/common/SsrOnlyContent/SsrOnlyContent'

import type { WhatOpenSection } from '../ProductInformationSection'
import { ModalSheetHeader } from './ModalSheetHeader'
import { useProductContextSelector } from '../../../contexts'

interface ExpandSectionProps {
  titleProductInformation: React.ReactNode
  sectionName: string
  eventName: string
  modalContent?: React.ReactNode
  ssrOnlyContent?: React.ReactNode
  whatOpenSection?: WhatOpenSection
  setWhatOpenSection?: (section: WhatOpenSection) => void
  modalTitle: string
}

export function ExpandSection({
  titleProductInformation,
  sectionName,
  modalContent,
  ssrOnlyContent,
  whatOpenSection,
  setWhatOpenSection,
  eventName,
  modalTitle,
}: ExpandSectionProps) {
  const headerRef = useRef<HTMLHeadElement>(null)
  const currentSku = useProductContextSelector((ctx) => ctx.currentSku)
  const { openModal, isModalOpen } = useModalSheet()
  const { pageData } = useGlobalContext()

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject({
    ...currentSku,
  })

  const featureDetailsObject = getFeatureObject({
    locationOnPage: null,
    name: eventName.charAt(0).toUpperCase() + eventName.slice(1),
    section: 'produto',
  })

  useEffect(() => {
    function hideImageWhenLoadingError() {
      const benefitImages = document.querySelectorAll(
        '.benefit-image'
      ) as unknown as HTMLImageElement[]

      benefitImages?.forEach((img) => {
        img.onerror = () => (img.style.display = 'none')
      })
    }

    hideImageWhenLoadingError()
  }, [isModalOpen])

  const EVENT_TRACK = useMemo(
    () =>
      makeEventTrack({
        eventAction: `${eventName} - ${!isModalOpen}`,
        eventPage: 'pagina de produto',
      }),
    [isModalOpen, eventName]
  )

  return (
    <div className="px-4">
      <section
        className="mt-6 py-md restructure-tablet:py-8 w-full border-b border-secondary max-w-[1648px] mx-auto"
        aria-label={`Seção ${modalTitle} do produto`}
        data-testid={`product-info-section-${sectionName}`}
      >
        <header ref={headerRef}>
          <button
            data-testid="buttonCollapseCharacteristics"
            name="Características do produto"
            className="flex justify-between items-center w-full"
            onClick={() => {
              if (setWhatOpenSection) {
                setWhatOpenSection(sectionName as WhatOpenSection)
              }

              openModal({
                content: <div className="mt-24 mb-[100px]">{modalContent}</div>,
                height: '90dvh',
                header: {
                  isVisible: true,
                  component: <ModalSheetHeader title={modalTitle} />,
                },
              })

              sendProductInformationSelectedEvent({
                featureDetailsObject,
                standardObject,
                productObject,
                clickedElement:
                  eventName === 'descricao'
                    ? eventName
                    : eventName.charAt(0).toUpperCase() + eventName.slice(1),
              })

              setTimeout(() => {
                headerRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'start',
                })
              }, 200)

              sendEvent(EVENT_TRACK)
            }}
          >
            <h2
              className="mobile-heading-title3 desktop-heading-title3 text-restructure-primary"
              aria-label={`Título ${sectionName} do produto`}
            >
              {titleProductInformation}
            </h2>
            <Icon
              name={
                whatOpenSection === sectionName && isModalOpen
                  ? 'MinusThin'
                  : 'PlusThin'
              }
              width={20}
              height={20}
              fill="#15181B"
              aria-label={
                isModalOpen
                  ? `Botão para minimizar as ${modalTitle} do produto`
                  : `Botão para maximizar as ${modalTitle} do produto`
              }
              data-testid={
                isModalOpen
                  ? 'minusIconCharacteristics'
                  : 'plusIconCharacteristics'
              }
            />
          </button>
        </header>

        {!isModalOpen && (
          <SsrOnlyContent
            sectionName={sectionName}
            testId={`ssr-only-content-${sectionName}`}
          >
            {ssrOnlyContent}
          </SsrOnlyContent>
        )}
      </section>
    </div>
  )
}

export default memo(ExpandSection)
